import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCrossCoinSignals } from '../../hooks/useCrossCoinSignals';
import { useLanguage } from '../../contexts/LanguageContext';
import './SignalBell.css';

function formatPrice(p) {
  const n = Number(p);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toPrecision(4)}`;
}

// Compact dollar volume — "$1.2M" / "$540K" / "$8B". Used in the signal
// row in place of the (always-fixed) "15m" timeframe so the badge carries
// actionable info: how big the bar that triggered the cross was.
function formatVolume(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function formatRelativeTime(ts, t) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return t('time.now', 'now');
  const diff = Math.max(0, Date.now() - n);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return t('time.secondsShort', '{{count}}s', { count: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('time.minutesShort', '{{count}}m', { count: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('time.hoursShort', '{{count}}h', { count: hr });
  return t('time.daysShort', '{{count}}d', { count: Math.floor(hr / 24) });
}

export default function SignalBell() {
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();
  const { signals, unreadCount, markAllRead } = useCrossCoinSignals();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const tooltip = t('chartPage.signalBell.tooltip', 'Cross-coin signals');
  const emptyLabel = t('chartPage.signalBell.empty', 'No signals yet. We will notify you when a cross is detected.');

  const safeSignals = useMemo(() => (Array.isArray(signals) ? signals : []), [signals]);

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) markAllRead();
      return next;
    });
  };

  const badge = unreadCount > 0 ? (unreadCount > 9 ? '9+' : String(unreadCount)) : null;

  return (
    <div
      className={`ct-signal-bell-wrap ${open ? 'open' : ''}`}
      ref={ref}
      data-testid="signal-bell-wrap"
    >
      <button
        type="button"
        className={`ct-signal-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={handleToggle}
        aria-label={tooltip}
        aria-haspopup="true"
        aria-expanded={open}
        data-tooltip={tooltip}
        data-testid="signal-bell-btn"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {badge != null && (
          <span className="ct-signal-bell-badge" data-testid="signal-bell-badge">{badge}</span>
        )}
      </button>

      {open && (
        <div className="ct-signal-dropdown" role="menu" data-testid="signal-bell-dropdown">
          <div className="ct-signal-dropdown-header">
            <span>{t('chartPage.signalBell.title', 'Recent signals')}</span>
          </div>
          {safeSignals.length === 0 ? (
            <div className="ct-signal-dropdown-empty" data-testid="signal-bell-empty">
              {emptyLabel}
            </div>
          ) : (
            <ul className="ct-signal-dropdown-list">
              {safeSignals.map((s, i) => {
                const dirClass = s.direction === 'long' ? 'long' : 'short';
                const dirLabel = (s.direction || '').toUpperCase();
                // Prefer dollar volume of the firing bar; gracefully fall
                // back to timeframe for legacy signals stored before the
                // server started emitting `volumeUsd`.
                const volText = formatVolume(s.volumeUsd);
                const badgeText = volText || s.timeframe || '15m';
                const priceText = formatPrice(s.price);
                const rel = formatRelativeTime(s.timestamp, t);
                return (
                  <li
                    key={`${s.coin}-${s.timestamp}-${i}`}
                    className="ct-signal-row"
                    data-testid="signal-bell-row"
                  >
                    <Link
                      to={localizedPath('/chart')}
                      className="ct-signal-row-link"
                      onClick={() => setOpen(false)}
                    >
                      <span className="ct-signal-coin">{s.coin}</span>
                      <span className={`ct-signal-direction ${dirClass}`}>{dirLabel}</span>
                      <span className={`ct-signal-tf ${volText ? 'is-volume' : ''}`}>{badgeText}</span>
                      <span className="ct-signal-price">@ {priceText}</span>
                      <span className="ct-signal-time">{rel}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
